import { useMutation } from '@apollo/client';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ProductionOrganizationInfo } from 'sr-types/lib/production/v1/graphql';
import { UserContext } from '../common/auth/UserContext';
import { BUTTON_VARIANTS } from '../common/constants/constants';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import { FormProvider } from '../common/form/FormContext';
import FormModal from '../common/form/FormModal';
import Validator from '../common/form/Validator';
import I18n, { useI18n } from '../common/i18n/I18n';
import { V } from '../common/Layout';
import KeyOrganizationEditor from '../organization/KeyOrganizationEditor';
import { EMPTY_STATE as EMPTY_ORGANIZATION } from '../organization/organization';
import SearchPanel from '../supply/searchkit/facets/SearchPanel';
import {
    getKeyOrganizationInput,
    getOrganizationWithKeyFlag,
    OrganizationWithRoles,
    ProductionOrganizationInfoWithKey,
    toProductOrganizationInput
} from './helpers/organizationUtils';
import { addOrganizationToProduction, client } from './helpers/production';
import { useSaveKeyOrganizations } from './helpers/productionApi';
import KeyOrganizationDataList from './KeyOrganizationDataList';
import KeyOrganizationListView from './KeyOrganizationListView';
import SearchOrCreateResult from './SearchOrCreateResult';

type AddKeyOrganizationsModalProps = {
    onClose: () => void;
    organizationsData: ProductionOrganizationInfo[];
    keyOrganizations: ProductionOrganizationInfo[];
    setViewState?: (viewState) => void;
    id?: string;
    open?: boolean;
    productionId?: string;
};

export default ({
    open,
    onClose,
    organizationsData,
    keyOrganizations,
    setViewState,
    productionId = undefined
}: AddKeyOrganizationsModalProps) => {
    const { activeOrganizationAccount } = useContext(UserContext);
    const [saveOrganizationToProduction] = useMutation(addOrganizationToProduction, { client });
    const { saveKeyOrganization } = useSaveKeyOrganizations();
    const searchKey = useI18n('production.header.form.searchKeyOrganization');
    const [isCreate, setIsCreate] = useState(false);
    const [searchAllOrganization, setSearchAllOrganization] = useState(false);
    const [isOrganizationSaving, setIsOrganizationSaving] = useState({ id: '', isSaving: false, productionRoles: [] });
    const [isFormSaving, setIsFormSaving] = useState(false);
    const [query, setQuery] = useState('');
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [organizationState, setOrganizationState] = useState({ ...EMPTY_ORGANIZATION });
    const orgWithKey: ProductionOrganizationInfoWithKey[] = useMemo(
        () => getOrganizationWithKeyFlag(organizationsData, keyOrganizations),
        [organizationsData, keyOrganizations]
    );
    const quickAddOrgRef = useRef(null);
    const cancelDialog = useI18n('dialog.cancel');

    const filteredOrgList: ProductionOrganizationInfoWithKey[] = useMemo(() => {
        const orgs = getOrganizationWithKeyFlag(organizationsData, keyOrganizations);
        if (query) {
            const searchWords = query.toLowerCase().split(' ');
            return orgs.filter((org) =>
                searchWords.every(
                    (word) =>
                        org.name.toLowerCase().includes(word) ||
                        org.productionRoles?.some((role) => role.toLowerCase().includes(word))
                )
            );
        }

        return orgs;
    }, [organizationsData, keyOrganizations, query]);

    useEffect(() => {
        if (Array.isArray(filteredOrgList) && filteredOrgList.length === 0) {
            setSearchAllOrganization(true);
        }
    }, [filteredOrgList]);

    const handleAfterRefetch = () => {
        setQuery('');
        setIsCreate(false);
        setSearchAllOrganization(false);
        setIsFormSaving(false);
        setIsOrganizationSaving({ id: '', isSaving: false, productionRoles: [] });
    };

    const handleFormModelOnClose = () => {
        setIsCreate(false);
        setSearchAllOrganization(false);
        onClose();
    };

    const onSuccess = (input: OrganizationWithRoles, id: string) => {
        const associatedOrganizationInput = toProductOrganizationInput(input, id);
        saveOrganizationToProduction({
            variables: { productionId, input: associatedOrganizationInput },
            context: {
                headers: {
                    ownerId: activeOrganizationAccount
                }
            }
        }).then(() => {
            const newKeyOrganizations = getKeyOrganizationInput(orgWithKey, undefined, undefined, {
                ...associatedOrganizationInput,
                isKey: true
            });
            saveKeyOrganization(productionId, newKeyOrganizations, true)
                .then(() => {
                    // Delay to ensure the backend has processed the new data before refetching as new org is created
                    setTimeout(() => {
                        client.refetchQueries({ include: [ENTITY_CONSTANTS.production] }).finally(() => {
                            handleAfterRefetch();
                        });
                    }, 300);
                })
                .catch(() => {
                    setIsFormSaving(false);
                });
        });
    };

    const onCancel = () => setIsFormSaving(false);

    const handleFormModalSave = () => {
        setIsFormSaving(true);
        quickAddOrgRef.current.handleSave(onSuccess, onCancel);
    };

    const AdditionalCancelAction = {
        label: cancelDialog,
        onClick: () => {
            setIsCreate(false);
            setSearchAllOrganization(false);
            setQuery('');
        },
        variant: BUTTON_VARIANTS.text
    };

    return (
        <FormProvider
            validationRules={{
                publicName: [Validator.RULES.isRequired]
            }}
            state={organizationState}
            setState={setOrganizationState}
        >
            <FormModal
                id={`keyDates`}
                width={'580px'}
                isOpen={open}
                onClose={() => handleFormModelOnClose()}
                isSaving={isFormSaving}
                isSaveDisabled={isSaveDisabled}
                title={<I18n token="form.organizations.addKeyOrganizations" />}
                loadingButtonVariant={BUTTON_VARIANTS.contained}
                hideButtons={!isCreate}
                onSave={handleFormModalSave}
                additionalActions={[AdditionalCancelAction]}
            >
                {isCreate ? (
                    <KeyOrganizationEditor
                        id={'quick-add-form'}
                        keyOrganizationFormFields
                        setIsSaveDisabled={setIsSaveDisabled}
                        ref={quickAddOrgRef}
                    />
                ) : (
                    <>
                        <SearchPanel
                            id={'org-serach'}
                            query={query}
                            setQuery={setQuery}
                            loading={false}
                            sx={{ width: '100%', p: 1 }}
                            searchPlaceholder={searchKey}
                            mobile={false}
                        />
                        {searchAllOrganization && query ? (
                            <KeyOrganizationListView
                                query={query}
                                setQuery={setQuery}
                                setIsCreate={setIsCreate}
                                setOrganizationState={setOrganizationState}
                                organizationState={organizationState}
                                setSearchAllOrganization={setSearchAllOrganization}
                                productionId={productionId!}
                                orgWithKey={orgWithKey}
                            />
                        ) : (
                            <V
                                sx={{
                                    pb: 2,
                                    overflow: 'auto'
                                }}
                                fill
                            >
                                <>
                                    <KeyOrganizationDataList
                                        isOrganizationSaving={isOrganizationSaving}
                                        setIsOrganizationSaving={setIsOrganizationSaving}
                                        productionId={productionId!}
                                        client={client}
                                        handleAfterRefetch={handleAfterRefetch}
                                        filteredOrgList={filteredOrgList}
                                        orgWithKey={orgWithKey}
                                        keyOrganizations={keyOrganizations}
                                        setViewState={setViewState}
                                    />
                                </>
                            </V>
                        )}
                    </>
                )}
                {query && !isCreate && (
                    <SearchOrCreateResult
                        query={query}
                        setAllSearch={setSearchAllOrganization}
                        setIsCreate={setIsCreate}
                        setState={setOrganizationState}
                        state={organizationState}
                        isSearchedInDirectory={searchAllOrganization}
                    />
                )}
            </FormModal>
        </FormProvider>
    );
};
